/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Locale, LocaleKeys } from '../../../../core/locale';
import type { TGetState, TivoPayloadAction } from '../../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../../core/utils';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../../list/list.reducers';
import {
    showGuideScreen,
    showHelpScreen,
    showPCForgottenPinHelpScreen,
    showPCLocksLimitsHelpScreen,
    showPCOnOffHelpScreen,
} from '../../../screens/screens.reducers';
import { SET_HELP_PC_OPTIONS_LIST, SET_HELP_PC_SELECTED_OPTION } from '../help.types';
import { IPcHelpSelectedOption } from './IPcHelpSelectedOption';
import { setPcHelpListAction, setSelectedPcHelpOptionAction } from './pchelp.actions';

interface IPcHelp {
    optionsList: Array<IPcHelpSelectedOption>;
    selectedOption?: IPcHelpSelectedOption;
}

const initialState: IPcHelp = {
    optionsList: [],
    selectedOption: undefined,
};

const pcHelpReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IPcHelp>>{},
) => {
    switch (type) {
        case SET_HELP_PC_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
                selectedOption: state.selectedOption ?? payload.optionsList[0],
            };
        case SET_HELP_PC_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        default:
            return state;
    }
};

export const setPcHelpOptionsList = () => (dispatch: Function) => {
    const settingsItemList: Array<IPcHelpSelectedOption> = [
        {
            value: Locale.getString(LocaleKeys.PC_HELP_SCREEN_TURN_ON_OFF_TITLE),
            preview: Locale.getString(LocaleKeys.PC_HELP_SCREEN_TURN_ON_OFF_PREVIEW_TEXT),
        },
        {
            value: Locale.getString(LocaleKeys.PC_HELP_SCREEN_SETTINGS_OPTION),
            preview: Locale.getString(LocaleKeys.PC_HELP_SCREEN_SETTINGS_OPTION_PREVIEW_TEXT),
        },
        {
            value: Locale.getString(LocaleKeys.PC_HELP_SCREEN_FORGOTTEN_TITLE),
            preview: Locale.getString(LocaleKeys.SETTINGS_HELP_PC_FORGOTTEN_PIN_PREVIEW_TEXT),
        },
    ];
    dispatch(setPcHelpListAction(settingsItemList));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackPcHelp = () => (dispatch: Function) => {
    dispatch(setSelectedPcHelpOptionAction());
    dispatch(showHelpScreen());
};

export const onKeyDownPcHelp = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.PC_HELP]: { position },
        },
        pcHelp: { optionsList },
    } = getState();
    if (position < optionsList.length - 1) {
        dispatch(setSelectedPcHelpOptionAction(optionsList[position + 1]));
        dispatch(onKeyDownList(ListTypes.PC_HELP));
    }
};

export const onKeyGuidePcHelp = () => (dispatch: Function) => {
    dispatch(setSelectedPcHelpOptionAction());
    dispatch(showGuideScreen());
};

export const onKeyOkPcHelp = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.PC_HELP]: { position },
        },
        pcHelp: { optionsList },
    } = getState();
    const selectedItem = optionsList[position].value;
    switch (selectedItem) {
        case Locale.getString(LocaleKeys.PC_HELP_SCREEN_TURN_ON_OFF_TITLE):
            dispatch(showPCOnOffHelpScreen());
            break;

        case Locale.getString(LocaleKeys.PC_HELP_SCREEN_SETTINGS_OPTION):
            dispatch(showPCLocksLimitsHelpScreen());
            break;

        case Locale.getString(LocaleKeys.PC_HELP_SCREEN_FORGOTTEN_TITLE):
            dispatch(showPCForgottenPinHelpScreen());
            break;
        default:
    }
};

export const onKeyUpPcHelp = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.PC_HELP]: { position },
        },
        pcHelp: { optionsList },
    } = getState();
    if (position > 0) {
        dispatch(setSelectedPcHelpOptionAction(optionsList[position - 1]));
        dispatch(onKeyUpList(ListTypes.PC_HELP));
    }
};

export const onMouseClickPcHelp = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value.includes(ListTypes.PC_HELP)) dispatch(onKeyOkPcHelp());
};

export const onMouseOutPcHelp = () => (dispatch: Function) => {
    dispatch(onMouseOutList());
};

export const onMouseOverPcHelp = () => (dispatch: Function, getState: TGetState) => {
    dispatch(onMouseOverList());
    const {
        list: {
            [ListTypes.PC_HELP]: { position },
        },
        pcHelp: { optionsList },
    } = getState();
    dispatch(setSelectedPcHelpOptionAction(optionsList[position]));
};

export default pcHelpReducer;
